/**
 * The extra leave rules a leave type may carry and a leave plan may override (ADR 0032, Phase 4c):
 * advance leave, carry-forward expiry, sandwich modes, request limits, combination limits, accrual
 * basis and special (event) leave.
 *
 * ONE SHAPE, TWO HOMES. `LeaveType` carries these as optional columns (the base value) and
 * `LeavePlanRule` as nullable overrides of the same names. Every field is NULLABLE and NULL MEANS
 * "not set": on a leave type it means today's behaviour, on a plan rule it means "inherit the leave
 * type's value" (ADR 0032, D1). With nothing set anywhere nothing changes — that is the whole
 * compatibility story, and `LEAVE_RULE_EXTENSION_NEUTRAL` is the proof: a rule at the neutral values
 * is a no-op.
 *
 * WHAT EACH FIELD MEANS (the semantics every implementation must agree with):
 *   negativeBalanceMaxDays  Advance leave. A request may take the balance down to minus this many
 *                           days; beyond it the excess is unpaid exactly as today. Unset = 0 (the
 *                           balance may not go below zero; the excess becomes unpaid).
 *   carryForwardExpiryDays  Days carried forward into the new leave year expire this many days after
 *   carryForwardExpiryMonthDay  the year starts, or on this MM-DD in the new year ("06-30"). At most
 *                           one of the two. 02-29 falls back to 02-28 in a non-leap year. Unset =
 *                           carried days live to the end of the new leave year, as today.
 *   sandwichMode            Which non-working days between two leave days are charged as leave.
 *                           `none`; `holidays_only`; `weekly_offs_only`; `only_above_x_days` (both
 *                           kinds, but only when the request's own working days reach
 *                           `sandwichMinDays`); `across_requests` (both kinds, and leave taken in an
 *                           ADJACENT request on the same leave type counts as the other side).
 *                           Unset = the leave type's legacy `sandwichPolicy` flag (true = both kinds,
 *                           within one request).
 *   minDaysPerRequest / maxDaysPerRequest   Bounds on the days a single request charges. Unset = none.
 *   maxRequestsPerMonth / maxRequestsPerYear  How many requests (pending or approved, not rejected or
 *                           withdrawn) may START in a calendar month / leave year. Unset = no limit.
 *   notCombinableWithLeaveTypeIds  Leave types that may not be taken back-to-back or overlapping with
 *                           this one. On a rule, `[]` means "none" and null means inherit.
 *   accrualBasis            `fixed` credits the quota on schedule (today); `days_worked` credits in
 *                           proportion to days actually worked in the period.
 *   prorateLeavers          True credits a person who leaves mid-year only the part of the quota they
 *                           served. Unset = today's behaviour (credited in full).
 *   accrualPerPayPeriod     True credits at the pay-period boundary of the legal entity rather than
 *                           the calendar boundary of `accrualFrequency`. Unset = false.
 *   leaveEventType          Marks a leave type as special leave for a life event. `none` and unset are
 *                           the same: an ordinary leave type.
 *   childCountLimit         The most children the event may be claimed for (maternity / paternity).
 *   quotaMode / eventQuotaDays  `per_event`: `eventQuotaDays` is granted afresh for each event;
 *                           `lifetime`: that many days over the whole employment. Unset = the
 *                           ordinary annual quota applies.
 *
 * NOT CROSS-CHECKED HERE: whether the pieces fit the leave type they are set on (a quota mode on a
 * type with no event). `leaveRuleExtensionProblems` checks what can be checked from the values alone.
 *
 * RN-SAFE: zod only, no Node built-ins. Day counts are decimal STRINGS (invariant 2's spirit; they
 * are never money, so no `Decimal` here) and never JS numbers.
 */

import { z } from 'zod';
import { leaveDaysSchema, uuidSchema } from './schemas/common.js';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const LEAVE_SANDWICH_MODES = [
  'none',
  'holidays_only',
  'weekly_offs_only',
  'only_above_x_days',
  'across_requests',
] as const;
export const leaveSandwichModeSchema = z.enum(LEAVE_SANDWICH_MODES);
export type LeaveSandwichMode = z.infer<typeof leaveSandwichModeSchema>;

export const LEAVE_ACCRUAL_BASES = ['fixed', 'days_worked'] as const;
export const leaveAccrualBasisSchema = z.enum(LEAVE_ACCRUAL_BASES);
export type LeaveAccrualBasis = z.infer<typeof leaveAccrualBasisSchema>;

export const LEAVE_EVENT_TYPES = [
  'none',
  'maternity',
  'paternity',
  'bereavement',
  'marriage',
  'other',
] as const;
export const leaveEventTypeSchema = z.enum(LEAVE_EVENT_TYPES);
export type LeaveEventType = z.infer<typeof leaveEventTypeSchema>;

export const LEAVE_QUOTA_MODES = ['per_event', 'lifetime'] as const;
export const leaveQuotaModeSchema = z.enum(LEAVE_QUOTA_MODES);
export type LeaveQuotaMode = z.infer<typeof leaveQuotaModeSchema>;

/**
 * A day in the year without a year: "06-30". Checked against a leap year so 02-29 is allowed
 * (it falls back to 02-28 when the year has none) and 02-30, 04-31 are not.
 */
export const monthDaySchema = z
  .string()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Must be MM-DD, e.g. 06-30')
  .refine((value) => {
    const parsed = new Date(`2000-${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(5, 10) === value;
  }, 'Not a real date');

// ---------------------------------------------------------------------------
// The fields (every one nullable: null = not set / inherit)
// ---------------------------------------------------------------------------

export const leaveRuleExtensionSchema = z.object({
  negativeBalanceMaxDays: leaveDaysSchema.or(z.literal('0')).nullable(),
  carryForwardExpiryDays: z.number().int().min(1).max(3650).nullable(),
  carryForwardExpiryMonthDay: monthDaySchema.nullable(),
  sandwichMode: leaveSandwichModeSchema.nullable(),
  /** Only with `sandwichMode: 'only_above_x_days'`. */
  sandwichMinDays: z.number().int().min(1).max(366).nullable(),
  minDaysPerRequest: leaveDaysSchema.nullable(),
  maxDaysPerRequest: leaveDaysSchema.nullable(),
  maxRequestsPerMonth: z.number().int().min(1).max(31).nullable(),
  maxRequestsPerYear: z.number().int().min(1).max(366).nullable(),
  /** `[]` is "none"; null is "inherit" (on a plan rule) / "none" (on a leave type). */
  notCombinableWithLeaveTypeIds: z.array(uuidSchema).max(50).nullable(),
  accrualBasis: leaveAccrualBasisSchema.nullable(),
  prorateLeavers: z.boolean().nullable(),
  accrualPerPayPeriod: z.boolean().nullable(),
  leaveEventType: leaveEventTypeSchema.nullable(),
  childCountLimit: z.number().int().min(1).max(20).nullable(),
  quotaMode: leaveQuotaModeSchema.nullable(),
  eventQuotaDays: leaveDaysSchema.nullable(),
});
export type LeaveRuleExtension = z.infer<typeof leaveRuleExtensionSchema>;

/** Nothing set: every field null. A rule or leave type at these values behaves exactly as before. */
export const LEAVE_RULE_EXTENSION_NEUTRAL: LeaveRuleExtension = {
  negativeBalanceMaxDays: null,
  carryForwardExpiryDays: null,
  carryForwardExpiryMonthDay: null,
  sandwichMode: null,
  sandwichMinDays: null,
  minDaysPerRequest: null,
  maxDaysPerRequest: null,
  maxRequestsPerMonth: null,
  maxRequestsPerYear: null,
  notCombinableWithLeaveTypeIds: null,
  accrualBasis: null,
  prorateLeavers: null,
  accrualPerPayPeriod: null,
  leaveEventType: null,
  childCountLimit: null,
  quotaMode: null,
  eventQuotaDays: null,
};

/** The same fields as a request body for a plan rule: absent = null = inherit. */
const extensionShape = leaveRuleExtensionSchema.shape;
export const leaveRuleExtensionBodyShape = {
  negativeBalanceMaxDays: extensionShape.negativeBalanceMaxDays.default(null),
  carryForwardExpiryDays: extensionShape.carryForwardExpiryDays.default(null),
  carryForwardExpiryMonthDay: extensionShape.carryForwardExpiryMonthDay.default(null),
  sandwichMode: extensionShape.sandwichMode.default(null),
  sandwichMinDays: extensionShape.sandwichMinDays.default(null),
  minDaysPerRequest: extensionShape.minDaysPerRequest.default(null),
  maxDaysPerRequest: extensionShape.maxDaysPerRequest.default(null),
  maxRequestsPerMonth: extensionShape.maxRequestsPerMonth.default(null),
  maxRequestsPerYear: extensionShape.maxRequestsPerYear.default(null),
  notCombinableWithLeaveTypeIds: extensionShape.notCombinableWithLeaveTypeIds.default(null),
  accrualBasis: extensionShape.accrualBasis.default(null),
  prorateLeavers: extensionShape.prorateLeavers.default(null),
  accrualPerPayPeriod: extensionShape.accrualPerPayPeriod.default(null),
  leaveEventType: extensionShape.leaveEventType.default(null),
  childCountLimit: extensionShape.childCountLimit.default(null),
  quotaMode: extensionShape.quotaMode.default(null),
  eventQuotaDays: extensionShape.eventQuotaDays.default(null),
};

/**
 * The fields as they sit on a leave type: all optional (a response from before this change, or a
 * create that sets none, is still valid), with `notCombinableWithLeaveTypeIds` a plain array because
 * the column is a list, not a nullable one — empty means none.
 */
export const leaveTypeRuleExtensionSchema = leaveRuleExtensionSchema
  .partial()
  .extend({ notCombinableWithLeaveTypeIds: z.array(uuidSchema).max(50).optional() });
export type LeaveTypeRuleExtension = z.infer<typeof leaveTypeRuleExtensionSchema>;

// ---------------------------------------------------------------------------
// Cross-field checks (shared by the plan-rule body and the leave-type bodies)
// ---------------------------------------------------------------------------

export interface LeaveRuleProblem {
  path: keyof LeaveRuleExtension;
  message: string;
}

const days = (value: string): number => Number.parseFloat(value);

/**
 * What can be wrong with a set of values on its own. Only fields that are present are judged, so
 * the same function serves a full rule and a partial PATCH.
 */
export function leaveRuleExtensionProblems(
  value: Partial<LeaveRuleExtension> | LeaveTypeRuleExtension,
): LeaveRuleProblem[] {
  const problems: LeaveRuleProblem[] = [];
  const set = <K extends keyof LeaveRuleExtension>(key: K) => (value as LeaveRuleExtension)[key] ?? null;

  if (set('carryForwardExpiryDays') !== null && set('carryForwardExpiryMonthDay') !== null) {
    problems.push({
      path: 'carryForwardExpiryMonthDay',
      message: 'Set either carryForwardExpiryDays or carryForwardExpiryMonthDay, not both',
    });
  }

  const mode = set('sandwichMode');
  const minDays = set('sandwichMinDays');
  if (mode === 'only_above_x_days' && minDays === null && 'sandwichMinDays' in value) {
    problems.push({
      path: 'sandwichMinDays',
      message: 'only_above_x_days needs sandwichMinDays',
    });
  }
  if (minDays !== null && 'sandwichMode' in value && mode !== 'only_above_x_days') {
    problems.push({
      path: 'sandwichMinDays',
      message: 'sandwichMinDays only goes with sandwichMode only_above_x_days',
    });
  }

  const min = set('minDaysPerRequest');
  const max = set('maxDaysPerRequest');
  if (min !== null && max !== null && days(min) > days(max)) {
    problems.push({
      path: 'minDaysPerRequest',
      message: 'minDaysPerRequest cannot exceed maxDaysPerRequest',
    });
  }

  const perMonth = set('maxRequestsPerMonth');
  const perYear = set('maxRequestsPerYear');
  if (perMonth !== null && perYear !== null && perMonth > perYear) {
    problems.push({
      path: 'maxRequestsPerMonth',
      message: 'maxRequestsPerMonth cannot exceed maxRequestsPerYear',
    });
  }

  const exclusive = set('notCombinableWithLeaveTypeIds');
  if (exclusive !== null && new Set(exclusive).size !== exclusive.length) {
    problems.push({
      path: 'notCombinableWithLeaveTypeIds',
      message: 'A leave type is listed twice',
    });
  }

  if (set('leaveEventType') === 'none') {
    for (const key of ['childCountLimit', 'quotaMode', 'eventQuotaDays'] as const) {
      if (set(key) !== null) {
        problems.push({ path: key, message: `${key} needs a leaveEventType other than none` });
      }
    }
  }
  if (set('quotaMode') !== null && set('eventQuotaDays') === null && 'eventQuotaDays' in value) {
    problems.push({ path: 'eventQuotaDays', message: 'quotaMode needs eventQuotaDays' });
  }

  return problems;
}

/** `.superRefine` callback that reports `leaveRuleExtensionProblems` against the right field. */
export function refineLeaveRuleExtension(
  value: Partial<LeaveRuleExtension> | LeaveTypeRuleExtension,
  ctx: z.RefinementCtx,
): void {
  for (const problem of leaveRuleExtensionProblems(value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [problem.path], message: problem.message });
  }
}
